import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "@a7y/tailwind-catalyst";
import type { AnnouncementDoc } from "@a7y/types";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmailPreview } from "../product-email/components/EmailPreview";

interface AnnouncementPreviewDialogProps {
  announcement: AnnouncementDoc;
  open: boolean;
  onClose: () => void;
}

/**
 * A7Y-5622: the email recipients will get, rendered by Functions with the
 * template Broadcast uses, from the saved announcement. Keyed on `updatedAt`,
 * so a saved edit to the summary lines is rendered afresh.
 */
export function AnnouncementPreviewDialog({
  announcement,
  open,
  onClose,
}: AnnouncementPreviewDialogProps) {
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const { data, isLoading, error } = useQuery({
    queryKey: ["announcement-preview", announcement.id, announcement.updatedAt],
    queryFn: () => api.previewAnnouncement(announcement.id),
    enabled: open,
  });

  return (
    <Dialog open={open} onClose={onClose} size="3xl">
      <DialogTitle>Email preview</DialogTitle>
      <DialogDescription>
        What recipients will get, addressed to you, with a placeholder
        preferences link. Save an edit to see it here.
      </DialogDescription>
      <DialogBody>
        {isLoading ? (
          <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
        ) : error ? (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error.message || "Failed to render the preview"}
          </div>
        ) : data ? (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
            <div className="flex items-center justify-between gap-4 border-b border-zinc-200 px-4 py-2 dark:border-zinc-700">
              <p className="truncate text-sm">
                <span className="text-zinc-500 dark:text-zinc-400">Subject: </span>
                <span className="font-medium text-zinc-900 dark:text-white">{data.subject}</span>
              </p>
              <div className="flex shrink-0 gap-1 rounded-lg border border-zinc-200 p-0.5 dark:border-zinc-700">
                {(["desktop", "mobile"] as const).map((mode) => (
                  <button
                    key={mode}
                    className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                      viewMode === mode
                        ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900"
                        : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                    }`}
                    onClick={() => setViewMode(mode)}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-4">
              <EmailPreview html={data.html} viewMode={viewMode} />
            </div>
          </div>
        ) : null}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
